'use client';

import { Heart, LucideIcon } from 'lucide-react';
import NextLink from 'next/link';
import { useLocale, useTranslations } from 'next-intl';

import { EmailSubscribeForm } from '@/components/email-subscribe-form';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { LanguageSwitcher } from '@/features/landing/components/language-switcher';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { Link } from '@/i18n/routing';
import { LANDING_LOCALES } from '@/lib/constants';

const socialLinks: { name: string; href: string; icon: LucideIcon }[] = [
  // { name: 'Twitter', href: '#', icon: Twitter },
  // {
  //   name: 'GitHub',
  //   href: '#',
  //   icon: Github,
  // },
  // { name: 'LinkedIn', href: '#', icon: Linkedin },
  // { name: 'YouTube', href: '#', icon: Youtube },
];

export function Footer() {
  const t = useTranslations('landing.footer');
  const tAuth = useTranslations('auth');
  const locale = useLocale() as 'de' | 'en' | 'pt';
  const { isAuthenticated, loading: authLoading } = useIsAuthenticated();

  // Build product links based on auth state
  const productLinks: { name: string; href: string }[] = [
    { name: t('columns.product.links.features'), href: '/features' },
  ];

  // Show dashboard link for authenticated users, login links for guests
  if (!authLoading && isAuthenticated) {
    productLinks.push({ name: tAuth('actions.goToDashboard'), href: '/app' });
  } else {
    productLinks.push(
      { name: t('loginLinks.employee'), href: '/auth/employee-login' },
      { name: t('loginLinks.admin'), href: '/auth/login' },
    );
  }

  const footerLinks: { [key: string]: { name: string; href: string }[] } = {
    product: productLinks,
    company: [
      // { name: t('columns.company.links.about'), href: '#about' },
      // { name: t('columns.company.links.careers'), href: '#careers' },
      { name: t('columns.company.links.blog'), href: '/blog' },
      { name: t('columns.company.links.imprint'), href: '/imprint' },
      // { name: t('columns.company.links.contact'), href: '#contact' },
    ],
    resources: [
      // { name: t('columns.resources.links.help'), href: '#help' },
      { name: t('columns.resources.links.documentation'), href: '/docs' },
      // { name: t('columns.resources.links.caseStudies'), href: '#case-studies' },
      // { name: t('columns.resources.links.guide'), href: '#guide' },
      // { name: t('columns.resources.links.webinars'), href: '#webinars' },
    ],
    legal: [
      { name: t('columns.legal.links.privacy'), href: '/privacy' },
      { name: t('columns.legal.links.terms'), href: '/terms' },
      // { name: t('columns.legal.links.cookies'), href: '#cookies' },
      // { name: t('columns.legal.links.security'), href: '#security' },
    ],
  };

  const renderLink = (link: { name: string; href: string }) => {
    // Use NextLink for external links, auth routes, and app routes (which don't have locale prefixes)
    const isNonLocalizedRoute =
      link.href.startsWith('http') || link.href.startsWith('/auth') || link.href.startsWith('/app');
    const Component = isNonLocalizedRoute ? NextLink : Link;

    // Add rel="nofollow" to auth routes since they're blocked by robots.txt
    const extraProps = link.href.startsWith('/auth') ? { rel: 'nofollow' } : {};

    return (
      <Component
        href={link.href}
        className="text-muted-foreground hover:text-primary transition-colors text-sm"
        {...extraProps}
      >
        {link.name}
      </Component>
    );
  };

  return (
    <footer className="border-t bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Newsletter Section */}
        <div className="mb-16">
          <div className="mx-auto max-w-2xl text-center">
            <h3 className="text-2xl font-bold mb-4 text-foreground">{t('newsletter.title')}</h3>
            <p className="text-muted-foreground mb-6">{t('newsletter.description')}</p>
            <EmailSubscribeForm
              source="landing-footer"
              placeholder={t('newsletter.placeholder')}
              buttonText={t('newsletter.button')}
              successMessage={t('newsletter.success') || 'Check your inbox!'}
              locale={locale}
            />
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid gap-8 grid-cols-4 lg:grid-cols-6">
          {/* Brand Column */}
          <div className="col-span-4 lg:col-span-2 max-w-2xl">
            <div className="flex items-center mb-4 max-lg:justify-center">
              <Link href="/" className="flex items-center cursor-pointer">
                <Logo variant="auto" width={140} height={36} />
              </Link>
            </div>
            <p className="text-muted-foreground mb-6 max-lg:text-center max-lg:flex max-lg:justify-center leading-relaxed">
              {t('description')}
            </p>
            <div className="flex space-x-4 max-lg:justify-center">
              {socialLinks.map((social) => (
                <Button key={social.name} variant="ghost" size="icon" asChild>
                  <NextLink
                    href={social.href}
                    aria-label={social.name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-primary transition-colors"
                  >
                    <social.icon className="h-5 w-5" />
                  </NextLink>
                </Button>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          <div className="max-md:col-span-2 lg:col-span-1">
            <h4 className="font-semibold mb-4 text-foreground">{t('columns.product.title')}</h4>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div className="max-md:col-span-2 lg:col-span-1">
            <h4 className="font-semibold mb-4 text-foreground">{t('columns.company.title')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div className="max-md:col-span-2 lg:col-span-1">
            <h4 className="font-semibold mb-4 text-foreground">{t('columns.resources.title')}</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div className="max-md:col-span-2 lg:col-span-1">
            <h4 className="font-semibold mb-4 text-foreground">{t('columns.legal.title')}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        <Separator className="my-8" />

        {/* Bottom Footer */}
        <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 text-muted-foreground text-sm">
            <div className="flex items-center gap-1">
              <span>{t('bottom.madeWith')}</span>
              <Heart className="h-4 w-4 text-red-500 fill-current" />
              <span>{t('bottom.inEurope')}</span>
            </div>
            <span className="hidden sm:inline">•</span>
            <span>
              © {new Date().getFullYear()} BiteClub. {t('bottom.rights')}
            </span>
          </div>

          <div className="flex items-center gap-6">
            <LanguageSwitcher supportedLocales={LANDING_LOCALES} />
            <div className="flex items-center space-x-6 text-sm text-muted-foreground">
              <Link href="/privacy" className="hover:text-foreground transition-colors">
                {t('bottom.privacy')}
              </Link>
              <Link href="/terms" className="hover:text-foreground transition-colors">
                {t('bottom.terms')}
              </Link>
              {/* <Link href="#cookies" className="hover:text-foreground transition-colors">
                {t('bottom.cookies')}
              </Link> */}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
